import { InfoCircleFilled } from '@ant-design/icons';
import { groupBy, orderBy } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';

import { TimetableRule, TimetableRuleState } from '../../graphql/api/generated';
import { Color, formatEnumValue, formatTimestamp } from '../../utils';
import { TextWithIconTooltip } from '../TextWithIconTooltip';

const GROUP_SORTING_WEIGHTS: { [key: string]: number } = {
  [TimetableRuleState.CONTROLLED]: 1,
  [TimetableRuleState.UNLOCKED]: 2,
  [TimetableRuleState.LOCKDOWN]: 3,
};

const DAY_ABBREVIATIONS = [
  'M',
  'Tu',
  'W',
  'Th',
  'F',
  'Sa',
  'Su',
];

interface ITimetableProps {
  timetable: TimetableRule[] | null | undefined;
  groupByState?: boolean;
}

export const Timetable: React.FC<ITimetableProps> = (props) => {
  const { groupByState } = props;
  const timetable = props.timetable || [];

  if (!timetable?.length) {
    return null;
  }

  const groupedSchedules = groupByState ? groupBy(timetable, r => r.state) : { ALL: timetable };

  const sortedEntries = orderBy(Object.entries(groupedSchedules), ([groupName]) => (
    GROUP_SORTING_WEIGHTS[groupName] || 0
  ));

  return (
    <>
      {sortedEntries.map(([groupName, schedules], groupIndex) => {
        return (
          <div
            key={groupName}
            style={{ marginBottom: groupIndex !== sortedEntries.length - 1 ? 10 : 0 }}
          >
            {groupByState && (
              <div style={{ fontWeight: 600 }}>
                {formatEnumValue(groupName)}
              </div>
            )}
            {schedules.map((schedule, scheduleIndex) => {
              const { days, start, end, startDate, endDate } = schedule;

              const dayAbbreviations = days.map(day => DAY_ABBREVIATIONS[day - 1]).join(', ');

              const parsedStartTime = moment(start, 'HH:mm');
              const parsedEndTime = moment(end, 'HH:mm');
              const times = `${parsedStartTime.format('h:mma')} - ${parsedEndTime.format('h:mma')}`;

              const isTempSchedule = !!(startDate && endDate);

              return (
                <div
                  key={scheduleIndex}
                  style={{ marginBottom: scheduleIndex !== schedules.length - 1 ? 10 : 0 }}
                >
                  {!isTempSchedule && (
                    <>
                      <div>{dayAbbreviations}</div>
                      <div>{times}</div>
                    </>
                  )}
                  {isTempSchedule && (
                    <div>
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {formatTimestamp(startDate)}
                      </span>
                      <span style={{ paddingLeft: 10, paddingRight: 10 }}>-</span>
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {new Date() > new Date(endDate) ? (
                          <TextWithIconTooltip
                            text={formatTimestamp(endDate)}
                            tooltip={{ title: 'This schedule has expired' }}
                            icon={
                              <InfoCircleFilled style={{ fontSize: '14px', color: Color.Gold }} />
                            }
                            containerStyle={{ display: 'inline-block' }}
                          />
                        ) : formatTimestamp(endDate)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};
